import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { getFirebaseConfig } from './config';
import { calculateSign } from './auth-utils';
import type { Game } from '../hooks/useGames';
import type { User } from '../types/user';
import type { Filter } from '../types/filter';
import { trackGameLaunch } from '../utils/analytics';
import log from '../utils/logger';

// Tipi per i parametri variabili (solo quelli che cambiano)
interface GameDataParams {
    page?: number;
    menu?: string;
    gameType?: string;
    accessToken?: string;
    staticData?: string;
    sign?: string;
}

// Tipi per il lancio gioco
interface LaunchGameParams {
    user?: string;
    carta?: string;
    platformID?: number;
    accessToken?: string;
    token?: string;
    launchCode: string;
    partnerID?: string;
    sign?: string;
}

// Tipo completo per la function (configurazione base + parametri variabili)
interface GameDataRequest extends GameDataParams {
    model: string;
    version: string;
    prodID: string;
    tf: string;
    lan: string;
    deviceID: string;
    requestId: string;
}

interface LaunchGameRequest extends LaunchGameParams {
    model: string;
    version: string;
    prodID: string;
    tf: string;
    lan: string;
    deviceID: string;
    requestId: string;
}

interface GameDataResponse {
    success?: boolean;
    data?: unknown;
    games?: Game[];
    menu?: unknown[];
    categories?: unknown[];
    filters?: Filter[];
}

interface LaunchGameResponse {
    status: number;
    data: {
        dataorarisposta?: string;
        esito: number;
        descrizione?: string;
        externaltoken?: string;
        externaltoken_sgp?: string;
        userSessionID?: string;
    };
    launchURL?: {
        demo?: string;
        real?: string;
        lobbyURL?: string;
    };
    token?: {
        accessToken: string,
        refreshToken: string,
        expiresIn: number
    },
    clearLoginParams?: boolean;
}

interface RefreshTokenResponse {
    status: number;
    accessToken: {
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
    };
}

interface GetBalanceRequest {
    model: string;
    version: string;
    prodID: string;
    tf: string;
    lan: string;
    deviceID: string;
    requestId: string;
    sign: string;
    user: string;
    carta: string;
    accessToken: string;
}

interface GetBalanceResponse {
    status: number;
    data: {
        esito: number;
        balance?: string;
        bonus?: string;
    };
}

// Callable functions
export const getGameData = httpsCallable<GameDataRequest, GameDataResponse>(
    functions,
    'game-gameData'
);

export const launchGame = httpsCallable<LaunchGameRequest, LaunchGameResponse>(
    functions,
    'gameLauncher-launchGame'
);

// Refresh token function
export const refreshToken = httpsCallable<{ refreshToken: string; sign: string; requestId: string } & ReturnType<typeof getFirebaseConfig>, RefreshTokenResponse>(
    functions,
    'authentication-refreshToken'
);

// Get balance function
export const getBalance = httpsCallable<GetBalanceRequest, GetBalanceResponse>(
    functions,
    'user-getBalance'
);

// Funzione helper aggiornata che include il token
export const fetchGameData = async (params: GameDataParams = {}) => {
    // Calcola requestId e sign usando il metodo Swift
    const { requestId, sign } = await calculateSign();

    const requestParams: GameDataRequest = {
        ...getFirebaseConfig(),
        page: 0,
        menu: "",
        gameType: "slot",
        staticData: "false",
        sign: sign,
        requestId: requestId,
        ...params
    };

    try {
        const result = await getGameData(requestParams);
        return result.data;
    } catch (error) {
        log.error('Error fetching game data:', error);
        throw error;
    }
};

// Nuova funzione per il lancio gioco
export const fetchLaunchGame = async (
    game: Game,
    gameUser?: User | null
) => {
    // Calcola requestId e sign usando il metodo Swift
    const { requestId, sign } = await calculateSign();

    // Determina quale token usare: accessToken ha priorità su token
    const hasAccessToken = gameUser?.authToken?.accessToken;
    const useAccessToken = hasAccessToken;

    const requestParams: LaunchGameRequest = {
        ...getFirebaseConfig(),
        user: gameUser?.user || "",
        carta: gameUser?.carta || "",
        platformID: game.platform.id,
        accessToken: useAccessToken ? gameUser?.authToken?.accessToken || "" : "",
        token: useAccessToken ? "" : gameUser?.token || "",
        launchCode: game.launchCode,
        partnerID: game.partnerID,
        sign: sign,
        requestId: requestId,
    };

    try {
        const result = await launchGame(requestParams);
        
        // Controlla se la sessione è scaduta (esito -607)
        if (result.data.data?.esito === -607) {
            // Ritorna un oggetto con flag per indicare che i parametri devono essere cancellati
            return {
                ...result.data,
                clearLoginParams: true
            };
        }
        
        // Track successful game launch (esito 1024 = success)
        if (result.data.data?.esito === 1024 && result.data.data?.externaltoken_sgp) {
            trackGameLaunch({
                source: "web",
                launchCode: game.launchCode,
                provider: game.provider,
                name: game.name,
                mode: 'real', // Assuming real mode when user is logged in
                platformID: game.platform.id,
                partnerID: game.partnerID
            });
        }
        
        return result.data;
    } catch (error) {
        log.error('Error launching game:', error);
        throw error;
    }
};

// Funzione per il refresh del token
export const fetchRefreshToken = async (refreshTokenValue: string) => {
    const { requestId, sign } = await calculateSign();
    
    const requestParams = {
        ...getFirebaseConfig(),
        refreshToken: refreshTokenValue,
        sign: sign,
        requestId: requestId,
    };
    
    try {
        const result = await refreshToken(requestParams);
        
        // La risposta ha una struttura diversa: result.accessToken contiene i token
        if (result.data?.status === 0 && result.data?.accessToken) {
            // Restituisce la struttura compatibile con LaunchGameResponse
            return {
                token: result.data.accessToken
            };
        }
        
        throw new Error('Invalid refresh token response');
    } catch (error) {
        log.error('Error refreshing token:', error);
        throw error;
    }
};

// Funzione per ottenere il balance
export const fetchGetBalance = async (gameUser: User) => {
    const { requestId, sign } = await calculateSign();
    
    const requestParams: GetBalanceRequest = {
        ...getFirebaseConfig(),
        user: gameUser.user,
        carta: gameUser.carta,
        accessToken: gameUser.authToken?.accessToken || "",
        sign: sign,
        requestId: requestId,
    };
    
    try {
        const result = await getBalance(requestParams);
        
        // Controlla se la sessione è scaduta (esito -607)
        if (result.data.data?.esito === -607) {
            throw { code: -607, data: result.data.data };
        }
        
        // Controlla se la risposta è valida (esito 1024)
        if (result.data.data?.esito === 1024) {
            return {
                balance: result.data.data.balance || '',
                bonus: result.data.data.bonus || ''
            };
        }
        
        throw new Error(`Invalid balance response:`);
    } catch (error) {
        log.error('Error fetching balance:', error);
        throw error;
    }
};